import {
  CSSProperties,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNumericFormat } from 'react-number-format';
import { useModal } from '@/hooks/useModal';
import {
  AlertBox,
  Button,
  CompareReportsModal,
  SvgIcon,
  TariffsPaymentMethod,
  TariffsProDetails,
  TariffsProPlusDetails,
} from '@/components';

import {
  ADDITION_MARK_TO_CATEGORY,
  getPlansAvailability,
  PlanCategoryEnum,
  tariffDisplayData,
} from '@/config';
import { useAppSelector } from '@/redux/hooks';
import {
  paymentSelectors,
  profileSelectors,
  userSelectors,
} from '@/redux/selectors';
import { AccountModesEnum, Plan } from '@/api';
import { PaymentContext, PaymentContextProps } from '@/contexts';
import {
  getFormattedDateWithTime,
  getPrice,
  sortPlansByCategory,
} from '@/helpers';
import parse from 'html-react-parser';
import { useOpenChat } from '@/hooks';
import { HubspotProvider } from '@aaronhayes/react-use-hubspot-form';
import { CompareTariffs } from '@/components/CompareTariffs';
import { TariffsTabs } from './components/TariffsTabs/TariffsTabs';
import { CreditsBenefits } from './components/CreditsBenefits/CreditsBenefits';
import { OrderSummary } from './components/OrderSummary/OrderSummary';
import { RefundNote } from './components/RefundNote/RefundNote';
import { KoloPromo } from './components/KoloPromo/KoloPromo';
import TariffEntry from './components/TariffEntry/TariffEntry';
import styles from './Tariffs.module.sass';
import { INDIVIDUAL_PRO_PLUS_PLAN } from './Tariffs.constants';

type TariffsStep = 'select' | 'payment';

type TariffsLocationState = {
  planId?: string | null;
  step?: TariffsStep;
};

interface TariffsProps {}

const Tariffs: FC<TariffsProps> = () => {
  const [currentTariff, setCurrentTariff] = useState<Plan | null>(null);
  const [step, setStep] = useState<TariffsStep>('select');
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<PlanCategoryEnum | null>(
    null,
  );
  const [availableCategories, setAvailableCategories] = useState<
    PlanCategoryEnum[]
  >([]);
  const [isCompareExpanded, setIsCompareExpanded] = useState(false);

  const locationState = (location.state || {}) as TariffsLocationState;
  const requestedPlanId = locationState.planId ?? null;
  const requestedStep = locationState.step;

  const {
    paymentPlans,
    paymentLoading,
    discount: userDiscount,
    discountDueTime,
  } = useAppSelector(paymentSelectors.getPaymentSlice);

  const planCategories = useMemo<Record<string, Plan[]>>(() => {
    if (!paymentPlans?.plans) return {};
    return sortPlansByCategory(paymentPlans.plans);
  }, [paymentPlans?.plans]);

  const { data: profileData } = useAppSelector(
    profileSelectors.getProfileSlice,
  );
  const isAiTraceOnly = useAppSelector(userSelectors.checkIfAiTraceOnly);
  const isProAccount = useAppSelector(profileSelectors.isPro);
  const {
    getBestDiscount,
    isCardPaymentAvailable,
    isDiscountWithExpirationTime,
  } = useContext(PaymentContext) as PaymentContextProps;

  const numberFormat = useNumericFormat({
    thousandSeparator: ',',
    decimalScale: 2,
  });

  const {
    isOpen: isOpenCompareReportsModal,
    onOpenModal: handleOpenCompareReportsModal,
    onCloseModal: handleCloseCompareReportsModal,
  } = useModal();

  const { handleOpenChat } = useOpenChat();

  useEffect(() => {
    if (profileData?.accountMode) {
      const { available, visible } = getPlansAvailability(
        profileData.accountMode,
        isAiTraceOnly,
      );

      setActiveCategory(() => available[0]);

      setAvailableCategories(() => [...available, ...visible]);
    }
  }, [profileData?.accountMode, isAiTraceOnly]);

  useEffect(() => {
    if (paymentLoading) return;

    if (activeCategory && planCategories[activeCategory]) {
      const visiblePlans = planCategories[activeCategory];

      const requestedPlan = visiblePlans.find(
        (plan) => plan.id === requestedPlanId,
      );
      const keptPlan =
        currentTariff &&
        visiblePlans.find((plan) => plan.id === currentTariff.id);

      setCurrentTariff(requestedPlan || keptPlan || visiblePlans[0]);

      if (!requestedPlan && !keptPlan && step === 'payment') {
        setStep('select');
      }
    }
  }, [requestedPlanId, planCategories, activeCategory, paymentLoading]);

  const currentTariffPrices = useMemo(() => {
    if (!currentTariff) return;

    const { price, pricePerCheck, oldPrice, discount } = getPrice(
      currentTariff.price,
      currentTariff.value,
      currentTariff.old_price,
      getBestDiscount(Number(currentTariff.price), userDiscount),
    );

    return { price, pricePerCheck, oldPrice, discount };
  }, [currentTariff, userDiscount, getBestDiscount]);

  const isPurchasable = useMemo(() => {
    if (currentTariff?.id === 'individual-pro-plus') {
      return false;
    }

    if (currentTariff) {
      const planType = ADDITION_MARK_TO_CATEGORY[currentTariff.addition];

      if (profileData?.accountMode) {
        return getPlansAvailability(
          profileData.accountMode,
          isAiTraceOnly,
        ).available.includes(planType);
      }
    }

    return false;
  }, [currentTariff, profileData?.accountMode, isAiTraceOnly]);

  const goToStep = useCallback((nextStep: TariffsStep) => {
    setStep(nextStep);
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    // No `currentTariff` check here: on a tab without plans (Business for a
    // Personal account) it is empty, and the request must still switch the
    // tab to the requested plan's category first.
    if (
      requestedStep !== 'payment' ||
      !activeCategory ||
      !paymentPlans?.plans
    ) {
      return;
    }
    if (paymentLoading) return;

    const requestedPlan = paymentPlans.plans.find(
      (plan) => plan.id === requestedPlanId,
    );
    const requestedCategory = requestedPlan
      ? ADDITION_MARK_TO_CATEGORY[requestedPlan.addition]
      : null;
    const isRequestedCategoryReachable =
      !!requestedCategory &&
      (requestedCategory === activeCategory ||
        availableCategories.includes(requestedCategory));

    if (requestedPlan && isRequestedCategoryReachable) {
      if (requestedCategory !== activeCategory) {
        setActiveCategory(requestedCategory);
        return;
      }

      if (currentTariff?.id !== requestedPlan.id) return;

      if (isPurchasable) {
        goToStep('payment');
      }
    }

    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: null,
    });
  }, [
    requestedStep,
    requestedPlanId,
    currentTariff?.id,
    activeCategory,
    availableCategories,
    isPurchasable,
    paymentLoading,
    paymentPlans?.plans,
  ]);

  useEffect(() => {
    if (!isPurchasable && step === 'payment') {
      setStep('select');
    }
  }, [isPurchasable]);

  const isPlaceholderCategory = useMemo(() => {
    if (
      activeCategory !== PlanCategoryEnum.PRO &&
      activeCategory !== PlanCategoryEnum.PRO_PLUS
    ) {
      return false;
    }
    if (profileData?.accountMode === AccountModesEnum.LITE) return true;
    return (planCategories[activeCategory]?.length ?? 0) === 0;
  }, [activeCategory, profileData?.accountMode, planCategories]);

  const hasTabs = useMemo(() => {
    return (availableCategories || []).length > 1;
  }, [availableCategories]);

  const isPaymentStep = step === 'payment' && !!currentTariff && isPurchasable;

  const isCardAvailable =
    !!currentTariffPrices && isCardPaymentAvailable(currentTariffPrices.price);

  const requestUpgrade = useCallback(() => {
    if (currentTariff?.id === 'individual-pro-plus') {
      handleOpenChat();
      return;
    }

    handleOpenCompareReportsModal();
  }, [currentTariff?.id, handleOpenCompareReportsModal]);

  const handleContinue = useCallback(() => {
    if (paymentLoading) return;

    if (isPurchasable) {
      goToStep('payment');
    } else {
      requestUpgrade();
    }
  }, [paymentLoading, isPurchasable, goToStep, requestUpgrade]);

  const handleBack = useCallback(() => goToStep('select'), [goToStep]);

  const handlePaymentSuccess = useCallback(
    () => goToStep('select'),
    [goToStep],
  );

  const toggleComparePlans = () => {
    setIsCompareExpanded((prev) => !prev);
  };

  const continueLabel = useMemo(() => {
    if (!isPurchasable) return t('translation.claim_plan_up');

    const amount = numberFormat.format?.(
      (currentTariffPrices?.price ?? 0).toString(),
    );

    return (
      <span className={styles.checkoutLabel}>
        <span>{t('translation.continue_to_payment_btn')}</span>
        <span className={styles.checkoutPrice}>${amount}</span>
      </span>
    );
  }, [isPurchasable, currentTariffPrices?.price, t]);

  const pageHead = (
    <div className={styles.head}>
      <h2 className={styles.headTitle}>{t('translation.buy_credits_title')}</h2>
      <p className={styles.headText}>
        {parse(
          t('translation.buy_credits_description', {
            trace: paymentPlans?.traceCheckCost || 10,
          }),
        )}
      </p>
    </div>
  );

  const discountAlert = isDiscountWithExpirationTime() && (
    <AlertBox type="info" className={styles.alert}>
      {{
        description: parse(
          t('translation.discount_expire_str', {
            date: getFormattedDateWithTime(discountDueTime * 1000),
          }),
        ),
        icon: '⚠️',
      }}
    </AlertBox>
  );

  return (
    <HubspotProvider>
      <div
        className={styles.tariffs}
        style={
          {
            '--current-tariff-color': activeCategory
              ? tariffDisplayData[activeCategory].color
              : '',
          } as CSSProperties
        }
      >
        {isPaymentStep && currentTariff && currentTariffPrices && (
          <>
            <div className={styles.backRow}>
              <button
                type="button"
                className={styles.back}
                onClick={handleBack}
                disabled={paymentLoading}
              >
                <SvgIcon name="arrow-left-short" className={styles.backIcon} />
                {t('translation.back_btn')}
              </button>
            </div>

            {pageHead}

            <div className={styles.inner}>
              <div className={styles.body}>
                {discountAlert}

                {isCardAvailable ? (
                  <>
                    <TariffsPaymentMethod
                      planId={Number(currentTariff.id)}
                      amount={currentTariffPrices.price}
                      onSuccess={handlePaymentSuccess}
                    />
                    <KoloPromo amount={currentTariffPrices.price} />
                  </>
                ) : (
                  <AlertBox type="info" className={styles.alert}>
                    {{
                      description: t('translation.card_payment_unavailable'),
                      icon: '💳',
                    }}
                  </AlertBox>
                )}

                <button
                  type="button"
                  className={styles.cryptoLink}
                  onClick={handleOpenChat}
                >
                  <span className={styles.cryptoText}>
                    {t('translation.pay_with_crypto_link')}
                  </span>
                  <SvgIcon name="arrow-right" className={styles.cryptoIcon} />
                </button>
              </div>

              <div className={styles.sidebar}>
                <OrderSummary
                  tariff={currentTariff}
                  prices={currentTariffPrices}
                />
                <RefundNote className={styles.refund} />
              </div>
            </div>
          </>
        )}

        {!isPaymentStep && (
          <>
            {pageHead}

            {!!activeCategory && hasTabs && (
              <div className={styles.nav}>
                <TariffsTabs
                  value={activeCategory}
                  className={styles.tabs}
                  categories={availableCategories}
                  onChange={setActiveCategory}
                />
              </div>
            )}

            {isPlaceholderCategory &&
              activeCategory === PlanCategoryEnum.PRO && <TariffsProDetails />}
            {isPlaceholderCategory &&
              activeCategory === PlanCategoryEnum.PRO_PLUS && (
                <TariffsProPlusDetails />
              )}

            {!isPlaceholderCategory && (
              <div className={styles.inner}>
                <div className={styles.body}>
                  {discountAlert}

                  {!!activeCategory && (
                    <div className={styles.items}>
                      {(planCategories[activeCategory] ?? []).map((item) => {
                        return (
                          <TariffEntry
                            key={item.id}
                            item={item}
                            planCategory={activeCategory}
                            isActive={item.id === currentTariff?.id}
                            onClick={() =>
                              !paymentLoading && setCurrentTariff(item)
                            }
                          />
                        );
                      })}

                      {activeCategory === PlanCategoryEnum.PRO_PLUS && (
                        <TariffEntry
                          key="individual-pro-plus"
                          item={INDIVIDUAL_PRO_PLUS_PLAN}
                          priceCustomText={
                            t('translation.individual_price') || ''
                          }
                          planCategory={activeCategory}
                          isActive={currentTariff?.id === 'individual-pro-plus'}
                          onClick={() =>
                            !paymentLoading &&
                            setCurrentTariff(INDIVIDUAL_PRO_PLUS_PLAN)
                          }
                        />
                      )}
                    </div>
                  )}

                  {currentTariff && (
                    <Button
                      fullwidth
                      type="primary-black"
                      corner="rect"
                      className={styles.checkoutButton}
                      disabled={paymentLoading}
                      onClick={handleContinue}
                    >
                      {continueLabel}
                    </Button>
                  )}

                  <RefundNote className={styles.refund} />
                </div>

                <div className={styles.sidebar}>
                  <CreditsBenefits />
                </div>
              </div>
            )}

            {activeCategory !== PlanCategoryEnum.PRO_PLUS && (
              <div className={styles.compare}>
                <CompareTariffs
                  isExpanded={isCompareExpanded}
                  onToggle={toggleComparePlans}
                  showPro={isProAccount}
                />
              </div>
            )}
          </>
        )}

        <CompareReportsModal
          isOpen={isOpenCompareReportsModal}
          onClose={handleCloseCompareReportsModal}
        />
      </div>

      <div id="hubspot-form" />
    </HubspotProvider>
  );
};

export default Tariffs;
